import { useEffect, useState } from "react";
import { Trash2, Loader2, Inbox } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const STATUSES = ["new", "contacted", "booked", "archived"];

const statusColor = {
  new: "bg-brand-crimson/15 text-brand-crimson border-brand-crimson/30",
  contacted: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  booked: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  archived: "bg-white/10 text-white/50 border-white/20",
};

export default function EnquiriesTab({ focusId = null, onFocused = null }) {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");
  const [highlightId, setHighlightId] = useState(null);

  const load = () => {
    api.get("/enquiries").then((r) => setItems(r.data)).catch(() => setItems([]));
  };
  useEffect(load, []);

  useEffect(() => {
    if (!focusId || items === null) return;
    if (filter !== "all") setFilter("all");
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid='admin-enquiry-row-${focusId}']`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        setHighlightId(focusId);
        setTimeout(() => setHighlightId(null), 3500);
      }
      if (onFocused) onFocused();
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusId, items]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/enquiries/${id}`, { status });
      setItems((prev) => prev.map((e) => (e.id === id ? { ...e, status } : e)));
      toast.success(`Marked as ${status}`);
    } catch (e) {
      toast.error("Could not update", { description: formatApiError(e) });
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/enquiries/${id}`);
      setItems((prev) => prev.filter((e) => e.id !== id));
      toast.success("Enquiry deleted");
    } catch (e) {
      toast.error("Could not delete", { description: formatApiError(e) });
    }
  };

  if (items === null) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-brand-crimson" data-testid="enquiries-loading-spinner" />
      </div>
    );
  }

  const visible = filter === "all" ? items : items.filter((e) => e.status === filter);

  return (
    <div data-testid="enquiries-tab">
      <div className="flex flex-wrap gap-2 mb-6">
        {["all", ...STATUSES].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            data-testid={`enquiries-filter-${s}`}
            className={`font-eyebrow text-[11px] uppercase tracking-[0.15em] font-bold px-4 py-2 rounded-md border transition-all ${
              filter === s ? "bg-brand-crimson border-brand-crimson text-white" : "border-brand-line text-white/60 hover:text-white"
            }`}
          >
            {s} {s !== "all" && `(${items.filter((e) => e.status === s).length})`}
          </button>
        ))}
      </div>

      {visible.length === 0 ? (
        <div className="rounded-lg border border-brand-line bg-brand-surface p-16 text-center" data-testid="enquiries-empty">
          <Inbox className="h-10 w-10 mx-auto text-white/30" />
          <p className="mt-4 text-white/50">No enquiries {filter !== "all" ? `with status "${filter}"` : "yet"}. New contact form messages land here.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {visible.map((e) => (
            <div key={e.id} className={`rounded-lg border bg-brand-surface p-5 transition-all duration-300 ${highlightId === e.id ? "border-brand-crimson ring-2 ring-brand-crimson/60" : "border-brand-line hover:border-brand-crimson/50"}`} data-testid={`admin-enquiry-row-${e.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-3">
                    <p className="font-semibold text-white" data-testid={`enquiry-name-${e.id}`}>{e.name}</p>
                    {e.urgent && <span className="text-[10px] font-eyebrow uppercase tracking-[0.2em] font-bold bg-brand-crimson text-white px-2.5 py-1 rounded-full">Urgent</span>}
                    <span className={`text-[10px] font-eyebrow uppercase tracking-[0.2em] font-bold px-2.5 py-1 rounded-full border ${statusColor[e.status] || statusColor.archived}`} data-testid={`enquiry-status-${e.id}`}>{e.status}</span>
                  </div>
                  <p className="mt-1 text-xs text-white/50">
                    <a href={`mailto:${e.email}`} className="hover:text-brand-crimson" data-testid={`enquiry-email-${e.id}`}>{e.email}</a>
                    {e.phone && <span> • {e.phone}</span>}
                    {e.event_date && <span> • needed {e.event_date}</span>}
                    {e.event_time && <span> • {e.event_time}</span>}
                    {e.location && <span> • {e.location}</span>}
                    {e.service && <span> • {e.service}</span>}
                  </p>
                  <p className="mt-3 text-sm text-slate-300 leading-relaxed" data-testid={`enquiry-message-${e.id}`}>{e.message}</p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <Select value={e.status} onValueChange={(v) => setStatus(e.id, v)}>
                    <SelectTrigger className="bg-brand-obsidian border-brand-line h-9 w-[130px] text-xs" data-testid={`enquiry-status-select-${e.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-brand-surface border-brand-line">
                      {STATUSES.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <button
                    onClick={() => remove(e.id)}
                    data-testid={`enquiry-delete-${e.id}`}
                    className="p-2 rounded-md text-white/40 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                    aria-label="Delete enquiry"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
